import type { ResolveItemNumbersResponse } from '@erp/rpc';

// ─── The catalog's multiple-items filter: pure value plumbing ────────────────

/**
 * What a paste into the multiple-items filter is split on: whitespace of any
 * kind (spaces, tabs, newlines — an Excel column or row), commas and
 * semicolons. Runs of separators collapse, so ", \n" between two values yields
 * no empty pill.
 */
export const ITEM_NUMBER_SPLIT_PATTERN = /[\s,;]+/;

/**
 * The most values the filter accepts — the `keys` cap on `listCatalog`.
 * Above it the page shows an inline error and fires no request at all.
 */
export const MAX_CATALOG_ITEM_NUMBERS = 10000;

/**
 * How many values render as individual pills. Above this the input collapses
 * its value display into one summary chip whose popover is the management
 * surface — a thousand pills would bury the search box.
 */
export const ITEM_NUMBER_PILL_LIMIT = 50;

/**
 * `MultiValueInput` speaks a comma-joined string; the filter state holds a
 * `string[]`. The split pattern eats commas and whitespace, so no pill can
 * contain either and the pair below round-trips losslessly.
 */
export function joinItemNumbers(values: readonly string[]): string {
  return values.join(', ');
}

export function splitItemNumbers(joined: string): string[] {
  return joined
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** What the resolver's response means for the catalog page. */
export interface ItemFilterResolution {
  /**
   * The distinct matched product keys — the `keys` filter for `listCatalog`,
   * and (their count) the result total: `countCatalog` deliberately doesn't
   * take `keys`, the client already knows the answer. Sorted so the same
   * resolution always produces the same query key.
   */
  productKeys: string[];
  /** The inputs (normalized) that matched no product. */
  unmatchedInputs: string[];
  /** How many inputs matched more than one product (duplicate `displaySku`s). */
  multiMatchInputCount: number;
}

/** Fold the resolver's (input × product) match pairs into the page's terms. */
export function deriveItemFilterResolution(
  response: ResolveItemNumbersResponse,
): ItemFilterResolution {
  const productKeys = new Set<string>();
  const matchCountByInput = new Map<string, number>();
  for (const match of response.matched) {
    productKeys.add(match.productKey);
    matchCountByInput.set(
      match.input,
      (matchCountByInput.get(match.input) ?? 0) + 1,
    );
  }
  let multiMatchInputCount = 0;
  for (const count of matchCountByInput.values()) {
    if (count > 1) multiMatchInputCount += 1;
  }
  return {
    productKeys: Array.from(productKeys).sort(),
    unmatchedInputs: response.unmatchedInputs,
    multiMatchInputCount,
  };
}
